import AddIcon from '@mui/icons-material/Add'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import {
  Box,
  Breadcrumbs,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material'
import axios from 'axios'
import type { ChangeEvent } from 'react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import ShareIcon from '@/assets/images/Share.svg'
import Button from '@/components/Button'
import CustomChip from '@/components/Chip'
import { IconButton } from '@/components/IconButton'
import type { PresignedUrlResponse } from '@/components/sidebar/Gallery'
import {
  useLazyGetImagePresignedUrlQuery,
  useUpdateCaseDetailMutation,
  useUploadCaseImagesMutation,
} from '@/store/apiService'
import {
  getProjectPermissions,
  getSupportedImagetypes,
  statusLabelMap,
  statusOptions,
} from '@/utils/constants'
import { getStatusStyle } from '@/utils/helper'

import type { Project } from '../projects'
import ShareProject from '../projects/ShareProject'
import ProjectDetailMenu from './ProjectDetailMenu'

const ProjectDetailHeader = ({
  refetch,
  caseDetails,
  onUploadInitiated,
  onUploadStart,
  updateProgress,
}: any) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'ProjectDetails',
  })

  const { t: translation } = useTranslation()

  const navigate = useNavigate()

  const { bgColor, textColor } = getStatusStyle(caseDetails?.status)

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [isShareDialog, setIsShareDialog] = useState(false)
  const [selectedCase, setSelectedCase] = useState<any | null>(null)

  const role = caseDetails?.role
  const permissions = getProjectPermissions(role)

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const [updateCaseDetail] = useUpdateCaseDetailMutation()

  const handleSelectChange = async (
    event: React.ChangeEvent<{ value: unknown }>,
  ) => {
    const newStatus = event.target.value as Project['status']

    try {
      await updateCaseDetail({
        caseId: caseDetails?.id,
        status: newStatus,
      } as any).unwrap()
      refetch()
      toast.success(translation('projectsTable.toast.statusUpdated'))
    } catch (error) {
      toast.error(translation('projectsTable.toast.statusUpdateFailed'))
    }
  }

  const handleShare = async () => {
    setSelectedCase({
      id: caseDetails?.id,
      patient_name: caseDetails?.name,
    })
    setIsShareDialog(true)
  }

  const [getImagePresignedUrl] = useLazyGetImagePresignedUrlQuery()
  const [uploadCaseImages] = useUploadCaseImagesMutation()

  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleImageUpload = async (
    file: File,
    fileIndex: number,
    onSuccess?: () => void,
  ) => {
    try {
      const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, ' ')
      const presignedData = (await getImagePresignedUrl({
        caseId: caseDetails?.id || '',
        fileName: sanitizedFileName,
      }).unwrap()) as PresignedUrlResponse

      toast.info(translation('projectsTable.toast.uploadingStarted'))

      await axios.put(presignedData.url, file, {
        headers: { 'Content-Type': file.type },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            )
            updateProgress(fileIndex, percentCompleted)
          }
        },
      })

      toast.info('Processing started.')
      await uploadCaseImages({
        caseId: caseDetails?.id || '',
        key: presignedData?.key,
      }).unwrap()

      onSuccess?.()
    } catch (error) {
      if (error instanceof Error) {
        toast.error(
          translation('projectsTable.toast.uploadingFailed', {
            error: error.message,
          }),
        )
      } else {
        toast.error(
          translation('projectsTable.toast.uploadingFailed', {
            error: 'Unknown error',
          }),
        )
      }
    }
  }

  const handleFileUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const basePath = '/' + location.pathname.split('/')[1]
      const files = Array.from(event.target.files)

      const validFiles = files.filter((file) => {
        const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, ' ')
        const fileExtension = sanitizedFileName.split('.').pop()?.toLowerCase()

        if (
          !fileExtension ||
          !getSupportedImagetypes(basePath).includes(fileExtension as any)
        ) {
          toast.error('Invalid file type')
          return false
        }
        return true
      })

      let startIndex = 0
      if (validFiles.length > 0) {
        startIndex = onUploadInitiated?.(validFiles.length) ?? 0
      }

      let successfulUploads = 0
      let pollingStarted = false

      validFiles.forEach((file, index) => {
        handleImageUpload(file, startIndex + index, () => {
          successfulUploads++
          if (successfulUploads > 0 && !pollingStarted) {
            pollingStarted = true
            onUploadStart?.(validFiles.length)
          }
        })
      })
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }

    handleMenuClose()
  }

  const handleUploadFromComputer = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  return (
    <>
      <Box display={'flex'} flexDirection={'column'} gap={1}>
        <Box>
          <Breadcrumbs sx={{ color: '#C6C6C8' }}>
            <Typography
              color="#C6C6C8"
              fontSize={'16px'}
              fontWeight={400}
              lineHeight={'24px'}
              onClick={() => navigate(`/projects?caseId=${caseDetails?.id}`)}
              sx={{ cursor: 'pointer' }}
            >
              {t('projects')}
            </Typography>
            <Typography
              color="#C6C6C8"
              fontSize={'16px'}
              fontWeight={400}
              lineHeight={'24px'}
            >
              {caseDetails?.name}
            </Typography>
          </Breadcrumbs>
        </Box>
        <Box>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Typography fontWeight={700} fontSize={'32px'}>
                {caseDetails?.name}
              </Typography>
              <CustomChip
                label={
                  caseDetails?.role
                    ? caseDetails.role.charAt(0).toUpperCase() +
                      caseDetails.role.slice(1)
                    : 'Owner'
                }
                size="medium"
                variant="outlined"
                sx={{
                  color: '#90CAF9',
                  fontSize: '13px',
                  fontWeight: 400,
                  lineHeight: '18px',
                  borderColor: '#90CAF9',
                }}
              />

              {caseDetails?.status &&
                (permissions?.canChangeStatus ? (
                  <Select
                    value={caseDetails.status}
                    onChange={handleSelectChange as any}
                    size="small"
                    variant="outlined"
                    sx={{
                      ...textStyle,
                      backgroundColor: bgColor,
                      color: textColor,
                      fontWeight: 600,
                      borderRadius: '20px',
                      '& .MuiSelect-icon': { color: textColor },
                      '& fieldset': { border: 'none' },
                      '& .MuiSelect-select': {
                        padding: '4px 12px',
                        whiteSpace: 'nowrap',
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          bgcolor: '#2a2a2a',
                          ...textStyle,
                        },
                      },
                    }}
                  >
                    {statusOptions.map((option) => {
                      const { textColor } = getStatusStyle(option)
                      return (
                        <MenuItem key={option} value={option} sx={textStyle}>
                          <Typography
                            sx={{ color: textColor, fontWeight: 500 }}
                          >
                            {statusLabelMap[option]}
                          </Typography>
                        </MenuItem>
                      )
                    })}
                  </Select>
                ) : (
                  <CustomChip
                    label={
                      statusLabelMap[caseDetails.status as Project['status']]
                    }
                    size="medium"
                    sx={{
                      bgcolor: bgColor,
                      color: textColor,
                      padding: '4px 8px',
                      fontSize: '14px',
                      fontWeight: 500,
                      lineHeight: '20px',
                      '&:hover': {
                        bgcolor: bgColor,
                      },
                    }}
                  />
                ))}

              {permissions?.canChangeDentistName && (
                <IconButton
                  sx={{ bgcolor: '#76768033', padding: '4px 8px' }}
                  onClick={handleMenuOpen}
                >
                  <MoreHorizIcon sx={{ color: 'white' }} />
                </IconButton>
              )}
              <ProjectDetailMenu
                anchorEl={anchorEl}
                handleMenuClose={handleMenuClose}
                caseDetails={caseDetails}
                refetch={refetch}
              />
            </Box>

            <Stack direction="row" spacing={2}>
              {permissions?.canShare && (
                <Button
                  variant="outlined"
                  startIcon={<img src={ShareIcon} alt="Share" />}
                  label={t('shareProject')}
                  onClick={handleShare}
                  sx={{
                    padding: '8px 22px',
                    fontSize: '15px',
                    lineHeight: '26px',
                  }}
                  textTransform="uppercase"
                />
              )}

              {permissions?.canAddImages && (
                <Button
                  variant="contained"
                  label={t('addImage')}
                  startIcon={<AddIcon sx={{ height: '24px', width: '24px' }} />}
                  onClick={handleUploadFromComputer}
                  sx={{
                    padding: '8px 22px',
                    fontSize: '15px',
                    lineHeight: '26px',
                  }}
                  textTransform="uppercase"
                />
              )}
            </Stack>
          </Box>
        </Box>

        <Box display={'flex'} flexDirection={'column'} gap={0.5}>
          <Typography fontSize={'14px'} fontWeight={400} color="#C6C6C8">
            {translation('caseInfoLayout.dentist')}:{' '}
            {caseDetails?.dentist?.name || '-'}
          </Typography>
        </Box>
      </Box>

      {isShareDialog && (
        <ShareProject
          open={isShareDialog}
          onClose={() => setIsShareDialog(false)}
          initialData={selectedCase}
          refetchRecipe={refetch}
        />
      )}

      <input
        type="file"
        multiple
        hidden
        ref={fileInputRef}
        onChange={handleFileUpload}
      />
    </>
  )
}

const textStyle = {
  color: 'white',
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '143%',
  letterSpacing: '0.17px',
}

export default ProjectDetailHeader
